import React, { useCallback, useState } from "react";
import { Stage, Layer, Image as KonvaImage } from "react-konva";
import useImage from "use-image";
import { Button } from "../../primary";
import style from "./WoundDotPlot.module.scss";
import constant from "../../../constants/constants.json";
import { angleToClockHour, computeLetterbox, insideImage, toImagePixels } from "./WoundDotPlotUtils";
import ArrowOverlay from "./ArrowOverlay";
import DotMarkers from "./DotMarkers";
import { StatusBanner } from "../StatusBanner";

const PHASE_DOTS = "dots";
const PHASE_ARROW = "arrow";

const WoundDotPlot = ({ imageSrc, isFallback = false, onExit, onClear, width = 540, height = 360 }) => {
  const [image] = useImage(imageSrc, "anonymous");

  const [phase, setPhase] = useState(PHASE_DOTS);
  const [dots, setDots] = useState([]);
  const [angleDeg, setAngleDeg] = useState(0);

  const actualW = image?.naturalWidth || width;
  const actualH = image?.naturalHeight || height;
  const { imgX, imgY, imgW, imgH } = computeLetterbox(actualW, actualH, width, height);

  const cx = imgX + imgW / 2;
  const cy = imgY + imgH / 2;

  const handleClick = useCallback(
    (e) => {
      if (isFallback || phase !== PHASE_DOTS) return;
      const pos = e.target.getStage().getPointerPosition();
      if (!pos || !insideImage(pos, imgX, imgY, imgW, imgH)) return;
      setDots((prev) => [...prev, { x: pos.x, y: pos.y }]);
    },
    [isFallback, phase, imgX, imgY, imgW, imgH]
  );

  const handleUndo = useCallback(() => {
    if (phase === PHASE_DOTS) {
      setDots((prev) => prev.slice(0, -1));
    } else {
      setPhase(PHASE_DOTS);
    }
  }, [phase]);

  const handleClear = useCallback(() => {
    setDots([]);
    setPhase(PHASE_DOTS);
    setAngleDeg(0);
    if (onClear) onClear();
  }, [onClear]);

  const handleNext = useCallback(() => {
    if (dots.length === 0) return;
    setPhase(PHASE_ARROW);
  }, [dots]);

  const handleAngleChange = useCallback((deg) => setAngleDeg(deg), []);

  const handleSubmit = useCallback(() => {
    if (!onExit || dots.length === 0) return;

    const headHour = angleToClockHour(angleDeg);
    const toeHour = angleToClockHour(angleDeg + 180);

    onExit({
      common: {
        arrow_to_head: headHour,
        arrow_to_toe: toeHour,
      },
      dots: dots.map((d) => toImagePixels(d, imgX, imgY, imgW, imgH, actualW, actualH)),
    });
  }, [onExit, dots, angleDeg, imgX, imgY, imgW, imgH, actualW, actualH]);

  const isNextEnabled = phase === PHASE_DOTS && dots.length > 0;
  const isSubmitEnabled = phase === PHASE_ARROW;
  const isUndoEnabled = dots.length > 0 || phase === PHASE_ARROW;

  return (
    <div data-testid="orientation-annotator" className={style.tracer_container}>
      <div className={style.tracer_header}>{phase === PHASE_DOTS && <>{constant.assessment.measurementDot}</>}</div>
      <div className={style.tracer_header}>{phase === PHASE_ARROW && <>{constant.assessment.measurementArrow}</>}</div>
      <StatusBanner status="info" layout="inline">
        <div>Click on the photo to place a dot at the center of the wound.</div>
        <div>
          Please be as accurate as possible when identifying the wound location, as this helps ensure accurate wound edge detection and measurement.
        </div>
      </StatusBanner>

      <div className={style.tracer_wrapper}>
        <div className={style.stage_container} data-testid="stage-container">
          <Stage
            width={width}
            height={height}
            className={`${style.stage_canvas} ${isFallback ? style.stage_canvas_fallback : ""} ${
              phase === PHASE_ARROW ? style.stage_canvas_arrow : ""
            }`}
            onClick={handleClick}
            data-testid="konva-stage"
          >
            <Layer>
              {image && <KonvaImage image={image} x={imgX} y={imgY} width={imgW} height={imgH} listening={false} />}
              <DotMarkers dots={dots} />
              {phase === PHASE_ARROW && (
                <ArrowOverlay cx={cx} cy={cy} angleDeg={angleDeg} imgW={imgW} imgH={imgH} onAngleChange={handleAngleChange} />
              )}
            </Layer>
          </Stage>
        </div>

        <div className={style.controls} data-testid="orientation-annotator-controls">
          {phase === PHASE_DOTS && (
            <Button data-testid="btn-next" disabled={!isNextEnabled} classes={{ className: `btn ${style.btn_add}` }} onClick={handleNext}>
              Next
            </Button>
          )}

          {phase === PHASE_ARROW && (
            <Button data-testid="btn-submit" disabled={!isSubmitEnabled} classes={{ className: `btn ${style.btn_add}` }} onClick={handleSubmit}>
              Submit
            </Button>
          )}

          <Button data-testid="btn-clear" classes={{ className: `btn ${style.no_btn_clr}` }} onClick={handleClear}>
            Clear
          </Button>

          <Button data-testid="btn-undo" disabled={!isUndoEnabled} classes={{ className: `btn ${style.no_btn_clr}` }} onClick={handleUndo}>
            Undo
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WoundDotPlot;
